import { Link } from "react-router-dom";
import { ArrowRight, Quote, Briefcase, GraduationCap, TrendingUp, Users } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { VerifyGate } from "../components/VerifyGate";
import { InstagramIcon } from "../components/BrandIcons";

const JOURNEY_ICONS = [Briefcase, Users, TrendingUp, GraduationCap];

const FounderContent = ({ d }) => (
    <div className="relative overflow-hidden" data-testid="founder-page">
        <div className="glow-orb right-[10%] top-[-5%] h-[380px] w-[380px] bg-cyan-500/12" />
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-center gap-14 lg:grid-cols-2">
                <Reveal>
                    <Eyebrow>The Founder</Eyebrow>
                    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="founder-heading">
                        Dharmendra Veer <span className="cyan-gradient-text">Silawat</span>
                    </h1>
                    <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.25em] text-cyan-400 notranslate">{d.role}</p>
                    {d.bio.map((p, i) => (
                        <p key={i} className="mt-4 leading-relaxed text-slate-400">{p}</p>
                    ))}
                    <div className="mt-8 flex flex-wrap gap-2.5">
                        {d.chips.map((t) => (
                            <span key={t} className="rounded-full border border-cyan-500/25 bg-cyan-500/10 px-3.5 py-1.5 text-xs font-medium text-cyan-300">
                                {t}
                            </span>
                        ))}
                    </div>
                    {d.instagram && (
                        <a
                            href={d.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="founder-instagram-link"
                            className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-pink-500/30 bg-gradient-to-r from-pink-500/10 to-purple-500/10 px-5 py-2.5 text-sm font-semibold text-pink-300 transition-colors hover:border-pink-500/60 hover:text-pink-200"
                        >
                            <InstagramIcon className="h-4.5 w-4.5 h-5 w-5" />
                            Follow on Instagram <span className="notranslate text-pink-400/80">{d.instagram_handle}</span>
                        </a>
                    )}
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="relative mx-auto w-full max-w-sm" data-testid="founder-card">
                        <div className="glow-card rounded-3xl p-1.5">
                            <div className="relative overflow-hidden rounded-[1.35rem]">
                                <img
                                    src={d.photo}
                                    alt={`${d.name} — Founder & Director, WorkAssign AI`}
                                    data-testid="founder-photo"
                                    className="aspect-[4/5] w-full object-cover"
                                    loading="lazy"
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#080C14] via-[#080C14]/60 to-transparent p-5 pt-16">
                                    <p className="font-display text-lg font-bold text-white">{d.name}</p>
                                    <p className="mt-0.5 text-[11px] uppercase tracking-widest text-slate-400">Founder & Director</p>
                                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-300">
                                        <Quote className="h-3.5 w-3.5 text-cyan-400" />
                                        Same Work · Less Cost · More Growth
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>

            <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {d.journey.map((j, i) => {
                    const Icon = JOURNEY_ICONS[i % JOURNEY_ICONS.length];
                    return (
                        <Reveal key={j.title} delay={i * 0.1}>
                            <div className="glow-card h-full rounded-2xl p-6" data-testid={`founder-journey-${i}`}>
                                <div className="mb-4 inline-flex rounded-xl bg-blue-500/10 p-3 text-blue-400">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <h3 className="font-display text-base font-bold text-white">{j.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{j.desc}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>

            <Reveal delay={0.15} className="mt-20">
                <div className="glow-card rounded-3xl p-10 text-center sm:p-14" data-testid="founder-vision">
                    <Quote className="mx-auto h-8 w-8 text-cyan-500/50" />
                    <p className="mx-auto mt-5 max-w-3xl font-display text-2xl font-bold leading-snug text-white sm:text-3xl">
                        "{d.quote}"
                    </p>
                    <p className="mt-6 text-sm text-slate-500">{d.ownership}</p>
                    <Link
                        to="/contact"
                        data-testid="founder-cta-button"
                        className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 px-8 py-3.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
                    >
                        Work With Us <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </Reveal>
        </section>
    </div>
);

export default function Founder() {
    return (
        <VerifyGate
            page="founder"
            title="Founder Journey"
            teaser="The story behind WorkAssign AI — 12+ years of experience and the vision for India's AI workforce. Verify your email to read it."
        >
            {(data) => <FounderContent d={data} />}
        </VerifyGate>
    );
}
